import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { computed, ref } from 'vue';

import { useGlobalStore } from '@/stores/globalStore';
import { useErrorStore } from '@/stores/components/errorStore';
import { useIdentityStore } from '@/stores/identityStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { useTrackingStore } from '@/stores/tracking/trackingStore';

import useDecimalHelpers from '@/composables/helpers/useDecimalHelpers';
import useWalletModal from '@/composables/ui/useWalletModal';
import useOrderCreation from '@/composables/api/useOrderCreation';
import { expressCheckoutEnabled } from '@/composables/router/handleExpressCheckout';
import { initCheckoutFlow, trackOrderPlaced, setExperimentVariant } from '@/composables/tracking/useDataDogTracking';
import { trackFeatureFlag } from '@/composables/tracking/useTracking';

export default function useAutomaticRedirect() {
  const globalStore = useGlobalStore();

  const { convertCoinAmountToDecimals } = useDecimalHelpers;
  const orderData = ref({});
  const covertedCoinAmount = computed(
    () => convertCoinAmountToDecimals(orderData.value.coinAmount, 8),
  );

  const formatRedirectUrl = (orderId) => {
    const {
      nativeSellDetails,
      walletAddress,
      blockchain: { code: blockchainCode } = {},
      coinType,
      walletAddressTag,
    } = orderData.value || {};

    const coinAmount = covertedCoinAmount.value;

    const redirectUrl = new URL(nativeSellDetails?.redirectSellUrl);

    const query = new URLSearchParams(redirectUrl.search);
    const params = {
      orderId,
      providerWalletAddress: walletAddress,
      blockchain: blockchainCode,
      coinType,
      coinAmount,
      providerWalletAddressTag: walletAddressTag,
      customerWalletAddress: nativeSellDetails?.customerWalletAddress,
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        query.set(key, value);
      }
    });

    redirectUrl.search = query.toString();

    return redirectUrl.toString();
  };

  const redirectToErrorPage = () => {
    const router = useRouter();
    const { t } = useI18n();
    const errorStore = useErrorStore();
      const error = {
          code: '50422',
          message: t('CoinDepositForm.error.sellWalletType'),
      };

      errorStore.$state.error = { ...error };

      router.push({
        name: 'error',
      });
  };

  /**
   * Redirect the user automatically to the specified URL
   */
  const redirectAutomatically = (orderId, orderDataProvided) => {
    orderData.value = orderDataProvided;
    const { redirectSellUrl } = orderData.value.nativeSellDetails;

    if (!redirectSellUrl) {
      // If order data provided has not sufficient data then redirect to error page
      redirectToErrorPage();
      return;
    }

    const redirectUrl = formatRedirectUrl(orderId, orderData);
    if (globalStore.getIsInIFrame) {
      window.parent.location = redirectUrl;
    }
    window.location.replace(redirectUrl);
  };

  const shouldRedirectToVerificationKyb = () => {
    const orderStore = useOrderFormStore();
    const hasEmail = !!orderStore.merchApiOrderData?.email;
    return !hasEmail && globalStore?.getMerchantSettings?.msisdnRequired;
  };

  const shouldRedirectToVerificationCodeKyb = () => {
    const orderStore = useOrderFormStore();
    const hasEmail = !!orderStore.merchApiOrderData?.email;
    return hasEmail && globalStore?.getMerchantSettings?.VALIDATE_EMAIL;
  };

  const setMerchDefaultsBeforeRedirect = () => new Promise((resolve) => {
    const orderStore = useOrderFormStore();
    const trackingStore = useTrackingStore();
    const form = ref({});
    const { updateStoreByMerchApiData } = useOrderCreation();
    updateStoreByMerchApiData(orderStore.merchApiOrderData, form, orderStore.getActiveStore);
    orderStore.setPspTypeCode(orderStore.merchApiOrderData.paymentType.toLowerCase());

    const isMobileRequired = orderStore.filterPaymentDataByActivePaymentName?.require_mobile
    ?? false;
    orderStore.setPspRequireMobile(isMobileRequired);
    trackingStore.setCustomProperty('Merchant API order defaults before redirect', {
      paymentMobileRequired: orderStore.filterPaymentDataByActivePaymentName?.require_mobile,
      isMobileRequired,
    });

    resolve();
  });

  const redirectForApiOrders = async (routerRef, fromRoute) => {
    const route = useRoute();
    const identityStore = useIdentityStore();
    const orderStore = useOrderFormStore();
    const { isWalletConfirmationModalRequired } = useWalletModal();

    const isAnApplePayOrderOrSessionDoesntExist = await orderStore.checkUserApplePaySession();
    if ((isAnApplePayOrderOrSessionDoesntExist)
      || !orderStore.canSkipOrderPageFromApiData
      || isWalletConfirmationModalRequired()
    ) {
      return;
    }

    setMerchDefaultsBeforeRedirect().then(async () => {
      const { redirectTo } = orderStore.merchApiOrderData;
      const paymentMethod = orderStore.merchApiOrderData?.paymentType?.toLowerCase() ?? 'unknown';

      trackFeatureFlag('expressCheckout', expressCheckoutEnabled() && redirectTo?.toLowerCase() === 'payment');

      if (expressCheckoutEnabled() && redirectTo?.toLowerCase() === 'payment') {
        setExperimentVariant('express_checkout', true);
        initCheckoutFlow({
          entryPoint: 'express_checkout',
          paymentMethod,
          flowVersion: '1.0',
        });
        trackOrderPlaced({ skip_order_page: true });
        orderStore.setExpressCheckoutEnabled(true);
        routerRef.push('/payment');
        return;
      }

      if (
        globalStore.getIsKyb && !fromRoute?.name?.includes('verification')
        && !fromRoute?.name?.includes('identity')
        && shouldRedirectToVerificationKyb()
      ) {
        initCheckoutFlow({
          entryPoint: 'merchant_api',
          paymentMethod,
          flowVersion: '1.0',
        });
        trackOrderPlaced({ skip_order_page: true });
        routerRef.push('/verification');
        return;
      }

      if (
        globalStore.getIsKyb && !fromRoute?.name?.includes('verification')
        && !fromRoute?.name?.includes('identity')
        && shouldRedirectToVerificationCodeKyb()
      ) {
        initCheckoutFlow({
          entryPoint: 'merchant_api',
          paymentMethod,
          flowVersion: '1.0',
        });
        trackOrderPlaced({ skip_order_page: true });
        routerRef.push('/verification/code');
        return;
      }

      if (redirectTo.indexOf('verification') >= 0) {
        initCheckoutFlow({
          entryPoint: 'merchant_api',
          paymentMethod,
          flowVersion: '1.0',
        });
        trackOrderPlaced({ skip_order_page: true });
        routerRef.push(`/${redirectTo}`);
        return;
      }

      initCheckoutFlow({
        entryPoint: 'merchant_api',
        paymentMethod,
        flowVersion: '1.0',
      });
      trackOrderPlaced({ skip_order_page: true });
      await identityStore.fetchIdentityDocuments();
      identityStore.fetchNextIdentityRoute(route).then((nextRoute) => {
        routerRef.push(nextRoute);
      });
    });
  };

  return {
    redirectAutomatically,
    redirectForApiOrders,
  };
}
